import L from "leaflet";
import {
    MapContainer,
    TileLayer,
    Marker,
    useMap,
    Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef, useLayoutEffect, useState } from "react";
import MarkerAvailable from "../../assets/img/icons/terminal-marker-available.png";
import MarkerUnavailable from "../../assets/img/icons/terminal-marker-unavailable.png";
import MarkerCharging from "../../assets/img/icons/terminal-marker-charging.png";
import { useTranslation } from "react-i18next";
import moment from "moment";
import classNames from "classnames";
import GeolocateIcon from "../../assets/img/icons/geolocate.svg";
import MarkerElecricityWater from "../../assets/img/icons/elecricity-water-marker.svg";
import MarkerElecricity from "../../assets/img/icons/elecricity-marker.svg";
import MarkerWater from "../../assets/img/icons/water-marker.svg";
import MarkerClusterGroup from "react-leaflet-cluster";

const CACHE_KEY = "terminal_map_cache";
const CACHE_EXPIRY = 15 * 60 * 1000; // 15 minutes

const FitBoundsToMarkers = ({
    terminals,
    focusedTerminal,
    userPosition,
    fitBoundsOnLoad,
}) => {
    const map = useMap();
    const previousTerminalsLength = useRef(0);
    const terminalsIds = useRef(new Set());

    // This effect handles initial bounds fitting and terminal list changes from search
    useLayoutEffect(() => {
        if (!map || terminals.length === 0) return;

        const currentIds = new Set(terminals.map((t) => t.id));

        const hasSignificantChanges =
            terminals.length !== previousTerminalsLength.current ||
            !Array.from(currentIds).every((id) => terminalsIds.current.has(id));

        if (hasSignificantChanges) {
            try {
                // Filter out any invalid coordinates first
                const validTerminals = terminals.filter(
                    (t) =>
                        typeof t.gpsLat === "number" &&
                        typeof t.gpsLon === "number" &&
                        !isNaN(t.gpsLat) &&
                        !isNaN(t.gpsLon),
                );

                if (validTerminals.length === 0) return;

                // Create array of LatLng points
                const points = validTerminals.map((t) =>
                    L.latLng(t.gpsLat, t.gpsLon),
                );

                // Create a bounds object from the points
                const bounds = L.latLngBounds(points);

                if (bounds && bounds.isValid()) {
                    // Add padding and fit map to these bounds
                    if (fitBoundsOnLoad) {
                        map.fitBounds(bounds, {
                            padding: [50, 50],
                            maxZoom: 15,
                            animate: true,
                        });
                    }

                    // Update refs to track the current terminal list
                    previousTerminalsLength.current = terminals.length;
                    terminalsIds.current = currentIds;
                }
            } catch (e) {
                console.error("Error fitting map bounds:", e);
            }
        }
    }, [map, terminals, fitBoundsOnLoad]);

    // This effect handles focusing on a specific terminal
    useEffect(() => {
        if (!map || !focusedTerminal) return;

        try {
            // If there's a focused terminal, center on it
            map.flyTo([focusedTerminal.gpsLat, focusedTerminal.gpsLon], 15, {
                animate: true,
                duration: 1,
            });
        } catch (e) {
            console.error("Error focusing terminal:", e);
        }
    }, [map, focusedTerminal]);

    useEffect(() => {
        if (!map || userPosition?.lat == null || userPosition?.lon == null) {
            return;
        }

        try {
            map.flyTo([userPosition.lat, userPosition.lon], 13, {
                animate: true,
                duration: 1,
            });
        } catch (e) {
            console.error("Error flying to user:", e);
        }
    }, [map, userPosition]);

    return null;
};

const TerminalMap = ({
    terminals,
    focusedTerminal,
    onMarkerClick,
    onGeolocClick,
    userPosition,
    hideMobile = true,
    hideLegend = false,
    hideGeoloc = true,
    boaterMarkers = false,
    grow = false,
    fitToMarkerBoundsOnLoad = true,
    enableClustering = false,
    enableScrollWheelZoom = false,
}) => {
    const INITIAL_ZOOM = 6;
    const INITIAL_POS = [46.23, 2.21]; // roughly center of France
    const { t } = useTranslation();

    const saveToCache = (terminalsData) => {
        if (!Array.isArray(terminalsData) || terminalsData.length === 0) return;

        try {
            const cacheData = {
                terminals: terminalsData,
                timestamp: Date.now(),
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            console.error("Error saving terminals to cache:", error);
        }
    };

    const loadFromCache = () => {
        try {
            const cachedData = localStorage.getItem(CACHE_KEY);
            if (!cachedData) return null;

            const { terminals: cachedTerminals, timestamp } =
                JSON.parse(cachedData);

            if (Date.now() - timestamp > CACHE_EXPIRY) {
                localStorage.removeItem(CACHE_KEY);
                return null;
            }

            return cachedTerminals;
        } catch (error) {
            console.error("Error loading terminals from cache:", error);
            return null;
        }
    };

    // Seed displayed terminals from cache on first render.
    const [displayedTerminals, setDisplayedTerminals] = useState(() => {
        const cachedTerminals = loadFromCache();
        return cachedTerminals && cachedTerminals.length > 0
            ? cachedTerminals
            : [];
    });
    const [prevTerminals, setPrevTerminals] = useState(terminals);

    // Show fresh terminals as soon as they arrive via props.
    if (terminals !== prevTerminals) {
        setPrevTerminals(terminals);
        if (Array.isArray(terminals) && terminals.length > 0) {
            setDisplayedTerminals(terminals);
        }
    }

    // Persist fresh terminals to cache (writing to an external store).
    useEffect(() => {
        if (Array.isArray(terminals) && terminals.length > 0) {
            saveToCache(terminals);
        }
    }, [terminals]);

    const renderMarkers = () => {
        if (
            !Array.isArray(displayedTerminals) ||
            displayedTerminals.length === 0
        )
            return null;

        const markers = displayedTerminals.map(
            (terminal) =>
                terminal.gpsLat !== undefined &&
                terminal.gpsLon !== undefined && (
                    <Marker
                        eventHandlers={{
                            add: (event) => {
                                event.target
                                    .getElement()
                                    ?.setAttribute(
                                        "data-terminal-id",
                                        terminal.cpoTerminalId || terminal.name || "",
                                    );
                            },
                            click: () => onMarkerClick(terminal),
                        }}
                        alt={terminal.cpoTerminalId || terminal.name || ""}
                        icon={
                            boaterMarkers
                                ? BoaterTerminalMarker(
                                      terminal,
                                      focusedTerminal &&
                                          terminal.id === focusedTerminal.id,
                                  )
                                : TerminalMarker(
                                      terminal,
                                      focusedTerminal &&
                                          terminal.id === focusedTerminal.id,
                                  )
                        }
                        key={terminal.id}
                        position={[terminal.gpsLat, terminal.gpsLon]}
                    >
                        {!boaterMarkers && (
                            <Tooltip
                                direction="top"
                                offset={[0, -20]}
                                opacity={1.0}
                                permanent={false}
                            >
                                <div className="terminal-tooltip">
                                    <div className="terminal-tooltip__top">
                                        <span
                                            className={`terminal-tooltip__status terminal-tooltip__status--${terminal.status}`}
                                        ></span>
                                        <span>
                                            {terminal.name} - {terminal.address}
                                        </span>
                                    </div>
                                    <div>
                                        {t("last_update")}:{" "}
                                        {moment
                                            .utc(terminal.lastUpdate?.date)
                                            .format(t("DATE_HOURS_FORMAT"))}
                                    </div>
                                </div>
                            </Tooltip>
                        )}
                    </Marker>
                ),
        );

        return markers.filter(Boolean);
    };

    return (
        <div
            className={classNames({
                "terminal-map": true,
                "terminal-map__keep-mobile": !hideMobile,
                "terminal-map__grow": grow,
            })}
        >
            <MapContainer
                center={INITIAL_POS}
                zoom={INITIAL_ZOOM}
                scrollWheelZoom={enableScrollWheelZoom}
                style={{ height: "100%", width: "100%", flex: "1 1 auto" }}
            >
                <FitBoundsToMarkers
                    terminals={displayedTerminals}
                    focusedTerminal={focusedTerminal}
                    userPosition={userPosition}
                    fitBoundsOnLoad={fitToMarkerBoundsOnLoad}
                />
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    referrerPolicy="strict-origin-when-cross-origin"
                />
                {userPosition?.lat != null && userPosition?.lon != null && (
                    <Marker
                        icon={UserMarker()}
                        position={[userPosition.lat, userPosition.lon]}
                    />
                )}
                {enableClustering ? (
                    <MarkerClusterGroup
                        chunkedLoading
                        disableClusteringAtZoom={15}
                        spiderfyOnMaxZoom={true}
                        showCoverageOnHover={false}
                        zoomToBoundsOnClick={true}
                    >
                        {renderMarkers()}
                    </MarkerClusterGroup>
                ) : (
                    renderMarkers()
                )}
            </MapContainer>
            <div className="terminal-map__overlay">
                {!hideGeoloc && onGeolocClick && (
                    <div className="terminal-map__geoloc">
                        <button
                            type="button"
                            title={t("geolocate")}
                            onClick={onGeolocClick}
                        >
                            <img src={GeolocateIcon} alt="" />
                        </button>
                    </div>
                )}
                {!hideLegend && (
                    <ul className="terminal-map__legend">
                        <li>
                            <img
                                src={MarkerAvailable}
                                alt=""
                                role="presentation"
                            />
                            {t("Available")}
                        </li>
                        <li>
                            <img
                                src={MarkerCharging}
                                alt=""
                                role="presentation"
                            />
                            <span>{t("Charging")}</span>
                        </li>
                        <li>
                            <img
                                src={MarkerUnavailable}
                                alt=""
                                role="presentation"
                            />
                            <span>{t("Unavailable")}</span>
                        </li>
                    </ul>
                )}
            </div>
        </div>
    );
};

const TerminalMarker = (terminal, focused = true) => {
    const statusIcons = {
        Available: MarkerAvailable,
        AvailableFull: MarkerAvailable,
        Unavailable: MarkerUnavailable,
        UnavailableFull: MarkerUnavailable,
        UnavailableOccupiedFull: MarkerUnavailable,
        Occupied: MarkerCharging,
        OccupiedFull: MarkerCharging,
        UnavailableOccupied: MarkerCharging,
        default: MarkerUnavailable,
    };
    const iconUrl = statusIcons[terminal.status] || statusIcons["default"];

    return L.divIcon({
        className: `leaflet-marker${focused ? " focused" : ""}`,
        iconSize: [40, 41],
        html: `<img src="${iconUrl}" width="40", height="41" alt="" />`,
    });
};

const BoaterTerminalMarker = (terminal, focused = true) => {
    let iconSrc = MarkerElecricityWater;
    if (terminal?.hasElectricity && !terminal?.hasWater) {
        iconSrc = MarkerElecricity;
    } else if (!terminal?.hasElectricity && terminal?.hasWater) {
        iconSrc = MarkerWater;
    }

    let statusClass = "";

    if (
        terminal.status === "UnavailableFull" ||
        terminal.status === "Offline"
    ) {
        statusClass = " status-unavailable";
    } else if (
        terminal.status === "OccupiedFull" ||
        terminal.status === "UnavailableOccupiedFull"
    ) {
        statusClass = " status-occupied";
    }

    return L.divIcon({
        className: `leaflet-marker leaflet-marker__boater${focused ? " focused" : ""}${statusClass}`,
        iconSize: [40, 40],
        html: `<img src="${iconSrc}" width="25" height="15" alt="" />`,
    });
};

const UserMarker = () => {
    return L.divIcon({
        className: `leaflet-marker leaflet-marker__user`,
        iconSize: [25, 25],
    });
};

export default TerminalMap;
